import { months } from '../modal-ventas/assets/date';

/**
 * Genera un mensaje de certificado para notificar el registro exitoso de un reporte
 *
 * @param empresaActual - Objeto que contiene la información de la empresa actual
 * @param empresaActual.periodo - Número del periodo/mes de la empresa
 * @param empresaActual.anio - Año del reporte de la empresa
 * @param empresaActual.razonSocial - Razón social de la empresa
 * @param empresaActual.nit - NIT de la empresa
 * @param lastReporte - (Opcional) Objeto que contiene información del último reporte registrado
 * @param lastReporte.periodo - Número del periodo/mes del último reporte
 * @param lastReporte.anio - Año del último reporte
 * @param lastReporte.fechaInicio - Fecha de inicio del último reporte
 *
 * @returns {string} - Mensaje formateado con la información del certificado, con líneas de máximo 80 caracteres
 *
 * @example
 * // Ejemplo de uso:
 * const mensaje = mensajeCertificado(
 *   {
 *     periodo: 3,
 *     anio: 2024,
 *     razonSocial: 'Empresa Ejemplo S.A.',
 *     nit: '123456789-0'
 *   },
 *   {
 *     periodo: 2,
 *     anio: 2024,
 *     fechaInicio: '2024-02-01 00:00:00'
 *   }
 * );
 *
 * @description
 * Esta función genera un mensaje estructurado que notifica el registro exitoso de un reporte.
 * Prioriza la información del `lastReporte` cuando está disponible, usando los datos de
 * `empresaActual` como valores por defecto. El mensaje incluye:
 * - Año y mes del reporte (convertido de número a nombre del mes)
 * - Información de la empresa (razón social y NIT)
 * - Fecha de registro
 * - Mensajes estándar de agradecimiento y contacto
 *
 * El texto se formatea automáticamente para que ninguna línea supere los 80 caracteres,
 * dividiendo las líneas largas en múltiples líneas más cortas.
 *
 * @see {@link ../modal-ventas/assets/date} Para la definición del array `months`
 */

export function mensajeCertificado(empresaActual, lastReporte?): string {
    const fechaActual = new Date();
    const formatoFecha = `${fechaActual.getFullYear()}-${(fechaActual.getMonth() + 1).toString().padStart(2, '0')}-${fechaActual.getDate().toString().padStart(2, '0')}`;

    const mesNumero = lastReporte ? lastReporte.periodo : empresaActual.mes;
    const mesNombre =
        months.find((m) => m.number === Number(mesNumero))?.name || mesNumero;

    const infoCertificado = {
        anio: lastReporte ? lastReporte.anio : empresaActual.anio,
        periodo: mesNombre,
        razonSocial: empresaActual.razonSocial,
        nit: empresaActual.nit,
        fechaInicio: lastReporte
            ? lastReporte.fechaInicio.split(' ')[0]
            : formatoFecha,
        fechaRegistro: lastReporte
            ? lastReporte.fechaRegistro.split(' ')[0]
            : formatoFecha,
        tamanoComercializador: lastReporte
            ? lastReporte.tamanoComercializador
            : null,
    };

    const wrapText = (text: string, maxLength: number): string[] => {
        const words = text.split(' ');
        const lines = [];
        let currentLine = words[0];

        for (let i = 1; i < words.length; i++) {
            if (currentLine.length + words[i].length + 1 <= maxLength) {
                currentLine += ' ' + words[i];
            } else {
                lines.push(currentLine);
                currentLine = words[i];
            }
        }
        lines.push(currentLine);
        return lines;
    };
    const periodoAjustado =
        infoCertificado.tamanoComercializador === 'Pequeño'
            ? `semestre de ${infoCertificado.periodo}`
            : infoCertificado.tamanoComercializador === 'Mediano'
              ? `trimestre de ${infoCertificado.periodo}`
              : `mes de ${infoCertificado.periodo}`;
    const partesMensaje = [
        `Se notifica que el reporte correspondiente al año ${infoCertificado.anio},`,
        `${periodoAjustado}, para la empresa ${infoCertificado.razonSocial}`,
        `con NIT ${infoCertificado.nit}, ha sido registrado exitosamente el día ${infoCertificado.fechaRegistro}.`,
        ``,
        `Gracias por su colaboración y por su confianza en nuestros servicios.`,
        `Para gestionar cualquier inquietud adicional, contáctese al correo`,
        `gestionsiriagro@upra.gov.co.`,
        ``,
        `Cordialmente,`,
    ];

    const lineasFinales = [];
    const maxLineLength = 80;

    partesMensaje.forEach((parte) => {
        if (parte.length <= maxLineLength || parte.trim() === '') {
            lineasFinales.push(parte);
        } else {
            lineasFinales.push(...wrapText(parte, maxLineLength));
        }
    });

    return lineasFinales.join('\n');
}
